import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { auth, api } from "@/lib/admin/api";
import { ToastHost, toast } from "@/components/admin/ui";
import { useLiveSiteBaseUrl } from "@/lib/admin/liveSite";

const NAV = [
  { to: "/owner-cabinet", label: "Products", icon: "M3 7l9-4 9 4v10l-9 4-9-4zM3 7l9 4 9-4M12 11v10", end: true },
  { to: "/owner-cabinet/categories", label: "Categories", icon: "M3 6h18M3 12h18M3 18h12" },
  { to: "/owner-cabinet/proof", label: "Testimonials / Proof", icon: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" },
  { to: "/owner-cabinet/homepage", label: "Homepage", icon: "M4 4h16v12H4zM4 16l4-4 3 3 5-5 4 4" },
  { to: "/owner-cabinet/media", label: "Media Library", icon: "M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" },
  { to: "/owner-cabinet/contact", label: "Contact Links", icon: "M2 12a10 10 0 1 0 20 0 10 10 0 1 0-20 0M2 12h20M12 2a15.3 15.3 0 0 1 0 20" },
  { to: "/owner-cabinet/inquiries", label: "Inquiries", icon: "M22 6l-10 7L2 6M2 6h20v12H2z" },
  { to: "/owner-cabinet/drops", label: "Draft Drops", icon: "M3 7h18l-1.4 11.2A2 2 0 0 1 17.6 20H6.4a2 2 0 0 1-2-1.8L3 7zM8 7V5a4 4 0 0 1 8 0v2", badge: "drops" },
  { to: "/owner-cabinet/marketing", label: "Email List", icon: "M2 12a10 10 0 1 0 20 0 10 10 0 1 0-20 0M12 2v20M2 12h20" },
  { to: "/owner-cabinet/referrals", label: "Referrals", icon: "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" },
  { to: "/owner-cabinet/settings", label: "Settings", icon: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" },
];

export default function AdminLayout() {
  const nav = useNavigate();
  const [open, setOpen] = React.useState(false);
  const liveBase = useLiveSiteBaseUrl();
  const [unviewedDrops, setUnviewedDrops] = React.useState(0);

  // Poll the drop-builder stats so the sidebar badge stays current.
  React.useEffect(() => {
    let alive = true;
    async function tick() {
      try {
        const s = await api.draftOrderStats();
        if (alive) setUnviewedDrops(s.unviewed || 0);
      } catch (e) { console.warn("[AdminLayout] draftOrderStats poll failed:", e); }
    }
    tick();
    const id = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Global keyboard shortcuts
  React.useEffect(() => {
    function onKey(e) {
      if (e.target?.tagName && /INPUT|TEXTAREA|SELECT/.test(e.target.tagName)) return;
      if ((e.metaKey || e.ctrlKey) && e.key === "z") {
        e.preventDefault();
        handleUndo();
      } else if (e.key === "n" || e.key === "N") {
        // Dispatch a custom event the Products page listens for
        window.dispatchEvent(new CustomEvent("admin:shortcut:new"));
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  async function handleUndo() {
    try {
      const r = await api.undo();
      toast(r.message || "Undone");
      window.dispatchEvent(new CustomEvent("admin:data:changed"));
    } catch (e) {
      toast(e.response?.data?.detail || "Nothing to undo", "warn");
    }
  }

  function editOnSite() {
    // Open the public Hostinger site as admin: bridge.js will validate
    // the token via /api/admin/auth/me and decorate cards with edit overlays.
    const token = auth.token;
    const base = (liveBase || "").replace(/\/+$/, "");
    const target = base ? `${base}/index.html` : "/2High/site/index.html";
    if (!token) {
      window.open(target, "_blank", "noopener");
      return;
    }
    const sep = target.includes("#") ? "&" : "#";
    window.open(`${target}${sep}2high-edit=${encodeURIComponent(token)}`, "_blank", "noopener");
  }

  return (
    <div className="min-h-screen bg-[#04060f] text-[#e8e4d8]" style={{ fontFamily: "'Inter',sans-serif" }}>
      <ToastHost />

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 bg-[#04060f]/95 backdrop-blur border-b border-[#c9b88a]/15">
        <div className="flex items-center gap-2">
          <button onClick={() => setOpen((o) => !o)} className="p-2 rounded-md border border-[#c9b88a]/20" data-testid="admin-menu-toggle">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
          </button>
          <span style={{ fontFamily: "'Cormorant Garamond',serif", fontStyle: "italic", fontSize: 22 }}>2<span style={{ color: "#c9b88a" }}>High</span></span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleUndo} data-testid="mobile-undo" className="text-[10px] tracking-[.25em] text-[#c9b88a]">↶ UNDO</button>
          <button onClick={() => { auth.logout(); }} className="text-[10px] tracking-[.25em] text-[#c9b88a]/70">LOGOUT</button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`${open ? "block" : "hidden"} lg:block fixed lg:sticky top-0 left-0 h-screen w-[240px] z-30 border-r border-[#c9b88a]/15 bg-[#04060f]/98 backdrop-blur flex-shrink-0 flex flex-col`}>
          <div className="p-6 border-b border-[#c9b88a]/12">
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontStyle: "italic", fontSize: 28 }}>2<span style={{ color: "#c9b88a" }}>High</span></div>
            <div className="text-[9px] tracking-[.5em] text-[#c9b88a]/60 mt-1">— OPERATOR HUB</div>
          </div>

          <nav className="p-3 space-y-1 flex-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-[11px] tracking-[.2em] uppercase transition-all ${
                    isActive ? "bg-[#c9b88a] text-[#04060f] font-medium" : "text-[#e8e4d8]/65 hover:text-[#e8e4d8] hover:bg-white/5"
                  }`
                }
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d={n.icon} />
                </svg>
                <span className="flex-1">{n.label}</span>
                {n.badge === "drops" && unviewedDrops > 0 && (
                  <span data-testid="drops-badge" className="inline-flex items-center justify-center px-1.5 min-w-[20px] h-[18px] rounded-full bg-[#c9b88a] text-[#04060f] text-[10px] font-bold tracking-normal font-mono">
                    {unviewedDrops > 9 ? "9+" : unviewedDrops}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="p-3 border-t border-[#c9b88a]/12 space-y-2">
            <button
              onClick={editOnSite}
              data-testid="edit-on-site"
              title="Opens the public site with admin overlay (only you can see it)"
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-md bg-white/5 hover:bg-white/10 border border-[#c9b88a]/25 text-[10px] tracking-[.28em] uppercase text-[#c9b88a]"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 3h7v7M21 3l-9 9M19 14v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6"/></svg>
              Open Site as Admin
            </button>
            <button
              onClick={handleUndo}
              data-testid="desktop-undo"
              title="Undo last action (⌘Z)"
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md hover:bg-white/5 text-[10px] tracking-[.28em] uppercase text-[#e8e4d8]/60"
            >↶ Undo last ·<span className="text-[#e8e4d8]/30">⌘Z</span></button>
            <button onClick={() => { auth.logout(); }} data-testid="admin-logout" className="w-full text-center text-[10px] tracking-[.28em] text-[#e8e4d8]/40 hover:text-[#c9b88a] py-1">Log out →</button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export const PageHead = ({ eye, title, sub, right }) => (
  <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
    <div>
      {eye && <div className="text-[10px] tracking-[.5em] text-[#c9b88a] uppercase mb-1.5">— {eye}</div>}
      <h1 className="text-2xl lg:text-3xl text-white" style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 300, lineHeight: 1 }}>{title}</h1>
      {sub && <p className="text-[12px] text-[#e8e4d8]/55 mt-1.5">{sub}</p>}
    </div>
    {right}
  </div>
);
